import { useEffect, useState, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  Box,
  Badge,
  useDisclosure,
  Divider,
  Progress,
  HStack,
  Link,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiFetchNotifications,
  apiAcknowledgeNotification,
} from 'api';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import type { Notification } from 'types/api.types';

export const BlockingNotificationModal = () => {
  const isNotificationsEnabled = useFeatureFlag('enable-big-communications');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mandatoryNotifications, setMandatoryNotifications] = useState<Notification[]>([]);
  const queryClient = useQueryClient();
  const isOpenRef = useRef(false);

  // Fetch unread mandatory notifications
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => apiFetchNotifications(1, 'unread', 10),
    refetchInterval: 300000, // Refetch every 5 minutes
  });

  // Filter for mandatory unread notifications and show modal immediately
  useEffect(() => {
    if (notificationsData?.results) {
      const mandatory = notificationsData.results.filter(
        (n: Notification) => n.requires_acknowledgement && n.status === 'unread'
      );
      setMandatoryNotifications(mandatory);
      
      // Show modal immediately if there are mandatory notifications
      if (mandatory.length > 0) {
        setCurrentNotification(mandatory[0]);
        setCurrentIndex(0);
        if (!isOpenRef.current) {
          onOpen();
          isOpenRef.current = true;
        }
      } else {
        // If no mandatory notifications, close modal
        if (isOpenRef.current) {
          onClose();
          setCurrentNotification(null);
          setCurrentIndex(0);
          isOpenRef.current = false;
        }
      }
    }
  }, [notificationsData, onOpen, onClose]);
  
  // Update ref when isOpen changes
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Acknowledge mutation
  const acknowledgeMutation = useMutation({
    mutationFn: apiAcknowledgeNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      handleNext();
    },
  });

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < mandatoryNotifications.length) {
      setCurrentNotification(mandatoryNotifications[nextIndex]);
      setCurrentIndex(nextIndex);
    } else {
      // All mandatory notifications acknowledged
      setCurrentNotification(null);
      setMandatoryNotifications([]);
      isOpenRef.current = false;
      onClose();
    }
  };

  const handleAcknowledge = () => {
    if (currentNotification) {
      acknowledgeMutation.mutate(currentNotification.id);
    }
  };

  // Don't render if feature flag is disabled
  if (!isNotificationsEnabled) return null;
  
  if (!currentNotification || mandatoryNotifications.length === 0) return null;

  const totalCount = mandatoryNotifications.length;
  const progressPercent = ((currentIndex + 1) / totalCount) * 100;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Cannot close - must acknowledge
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size="xl"
    >
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(10px)" />
      <ModalContent maxH="80vh" display="flex" flexDirection="column">
        <ModalHeader>
          <VStack align="start" spacing={3}>
            <Box p={3} bg="red.50" borderRadius="md" borderWidth="2px" borderColor="red.200" w="full">
              <HStack spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color="red.700">
                  You have {totalCount} notification{totalCount > 1 ? 's' : ''} that must be read before proceeding.
                </Text>
              </HStack>
              <Text fontSize="sm" color="red.600" mt={1}>
                You cannot access other parts of the app until you acknowledge {totalCount > 1 ? 'these notifications' : 'this notification'}.
              </Text>
            </Box>
            <HStack spacing={2} flexWrap="wrap">
              {currentNotification.is_edited && (
                <Badge colorScheme="blue" fontSize="md">
                  Edited
                </Badge>
              )}
            </HStack>
            <Text fontSize="xl" fontWeight="bold" wordBreak="break-word">
              {currentNotification.title}
            </Text>
            {totalCount > 1 && (
              <>
                <Text fontSize="sm" color="gray.500">
                  Notification {currentIndex + 1} of {totalCount}
                </Text>
                <Progress
                  value={progressPercent}
                  size="sm"
                  colorScheme="blue"
                  w="full"
                  borderRadius="md"
                />
              </>
            )}
          </VStack>
        </ModalHeader>
        
        <Divider />
        
        <ModalBody overflow="auto" flex="1" py={6}>
          <VStack align="start" spacing={4}>
            {currentNotification.summary && (
              <Box
                w="full"
                p={4}
                bg="gray.50"
                borderRadius="md"
                borderLeftWidth="4px"
                borderLeftColor="blue.500"
              >
                <Text fontWeight="semibold" color="gray.700" fontSize="md" mb={2}>
                  Summary
                </Text>
                <Text fontSize="md" color="gray.600">
                  {currentNotification.summary}
                </Text>
              </Box>
            )}

            <Divider />

            <Box w="full">
              <Text fontWeight="semibold" fontSize="md" mb={2} color="gray.700">
                Details
              </Text>
              <Box
                w="full"
                dangerouslySetInnerHTML={{ __html: currentNotification.content }}
                sx={{
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 'md',
                    my: 2,
                  },
                  '& a': {
                    color: 'blue.500',
                    textDecoration: 'underline',
                  },
                  '& h1, & h2, & h3': {
                    fontWeight: 'bold',
                    my: 2,
                  },
                  '& ul, & ol': {
                    pl: 6,
                  },
                  '& p': {
                    my: 2,
                  },
                }}
              />
            </Box>
            
            {/* Attachments Section */}
            {currentNotification.attachments && currentNotification.attachments.length > 0 && (
              <>
                <Divider />
                <Box w="full">
                  <Text fontWeight="semibold" mb={2}>Attachments</Text>
                  <VStack align="stretch" spacing={2}>
                    {currentNotification.attachments.map((attachment) => (
                      <HStack
                        key={attachment.id}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        justify="space-between"
                      >
                        <HStack>
                          <DownloadIcon />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {attachment.filename}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {(attachment.file_size / 1024).toFixed(2)} KB
                            </Text>
                          </VStack>
                        </HStack>
                        <Link href={attachment.file} isExternal>
                          <Button size="sm" colorScheme="blue" variant="outline">
                            Download
                          </Button>
                        </Link>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        
        <Divider />
        
        <ModalFooter>
          <VStack w="full" spacing={2}>
            <Button
              colorScheme="blue"
              w="full"
              size="lg"
              onClick={handleAcknowledge}
              isLoading={acknowledgeMutation.isPending}
            >
              I Acknowledge - {totalCount > 1 && currentIndex < totalCount - 1 ? 'Next' : 'Continue'}
            </Button>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              You must acknowledge this notification to continue using the application
            </Text>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
